import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, LogOut, AlertCircle, AlertTriangle, Eye, EyeOff, Settings, Smartphone, Monitor, Tablet, Globe, BarChart3, FileText, Link2 } from 'lucide-react';
import apiService from "./services/apiService";
import UserStats from './components/UserStats';
import PatchNotesPage from './PatchNotesPage';

const SettingsModal = ({ user, onClose, onLogout }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showConfirmLogout, setShowConfirmLogout] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [disconnectingToken, setDisconnectingToken] = useState(null);

    // Liaison compte Discord
    const [discordLink, setDiscordLink] = useState(null); // null tant que non chargé
    const [discordCode, setDiscordCode] = useState('');
    const [linkingDiscord, setLinkingDiscord] = useState(false);
    const [discordError, setDiscordError] = useState('');

    // ✨ NOUVEAU : Système d'onglets
    const [activeTab, setActiveTab] = useState('settings'); // 'settings' ou 'stats'

    // Bloquer le scroll du body quand le modal est ouvert
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // Charger les sessions au montage
    useEffect(() => {
        if (user && user.id) {
            loadSessions();
        }
    }, [user?.id]);

    // Charger le statut de liaison Discord au montage
    useEffect(() => {
        if (user && user.id) {
            loadDiscordLink();
        }
    }, [user?.id]);

    const loadDiscordLink = async () => {
        try {
            const data = await apiService.getDiscordLinkStatus();
            if (data.success) {
                setDiscordLink(data);
            }
        } catch (err) {
            console.error('Erreur chargement lien Discord:', err);
        }
    };

    const handleConfirmDiscordLink = async () => {
        if (!discordCode.trim()) return;

        setLinkingDiscord(true);
        setDiscordError('');
        try {
            const data = await apiService.confirmDiscordLink(discordCode.trim());

            if (data.success) {
                setDiscordCode('');
                loadDiscordLink();
            } else {
                setDiscordError(data.message || 'Code invalide ou expiré.');
            }
        } catch (err) {
            setDiscordError('Erreur lors de la liaison du compte.');
        } finally {
            setLinkingDiscord(false);
        }
    };

    const handleUnlinkDiscord = async () => {
        setLinkingDiscord(true);
        try {
            const data = await apiService.unlinkDiscord();
            if (data.success) {
                loadDiscordLink();
            }
        } catch (err) {
            console.error('Erreur déliaison Discord:', err);
        } finally {
            setLinkingDiscord(false);
        }
    };

    const loadSessions = async () => {
        if (!user || !user.id) return;

        setLoadingSessions(true);
        try {
            const data = await apiService.getUserSessions(user.id);

            if (data.success) {
                setSessions(data.sessions);
            }
        } catch (err) {
            console.error('Erreur chargement sessions:', err);
        } finally {
            setLoadingSessions(false);
        }
    };

    const handleDisconnectSession = async (session) => {
        setDisconnectingToken(session.id ?? session.token);
        try {
            const data = await apiService.disconnectSession(session);

            if (data?.success) {
                loadSessions();
            } else {
                setError(data?.message || 'Erreur lors de la déconnexion');
            }
        } catch (err) {
            // Reprendre le message du serveur : « erreur de connexion » pour
            // un 403 envoyait sur une fausse piste.
            setError(err?.status
                ? `Déconnexion refusée (${err.status}) : ${err.message}`
                : 'Serveur injoignable');
        } finally {
            setDisconnectingToken(null);
        }
    };

    const handleLogoutAllSessions = async () => {
        if (!user || !user.id) return;

        setShowConfirmLogout(false);
        setLoading(true);
        setError('');

        try {
            const data = await apiService.logoutAllSessions(user.id);

            if (data.success) {
                onLogout();
            } else {
                setError(data.message || 'Erreur lors de la déconnexion');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const getDeviceIcon = (device, isPWA) => {
        if (isPWA) return <Tablet className="w-5 h-5" />;
        if (device === 'mobile') return <Smartphone className="w-5 h-5" />;
        return <Monitor className="w-5 h-5" />;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'À l\'instant';
        if (minutes < 60) return `Il y a ${minutes} min`;
        if (hours < 24) return `Il y a ${hours}h`;
        if (days < 7) return `Il y a ${days}j`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    if (!user) {
        return null;
    }

    const inputStyle = {
        width: '100%',
        background: 'var(--glass)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--r-md)',
        padding: '10px 14px',
        color: 'var(--text-1)',
        fontSize: '0.9375rem',
        outline: 'none',
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16,
            }}
        >
            <div
                style={{
                    background: 'var(--bg-modal)',
                    borderRadius: 'var(--r-2xl)',
                    maxWidth: 720, width: '100%',
                    border: '1px solid var(--glass-border)',
                    boxShadow: 'var(--shadow-xl)',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '20px 24px',
                        borderBottom: '1px solid var(--glass-border)',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div
                            style={{
                                width: 42, height: 42, borderRadius: 12,
                                background: 'linear-gradient(135deg, var(--accent-deep), var(--accent))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Settings style={{ width: 20, height: 20, color: '#fff' }} />
                        </div>
                        <div>
                            <h2 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.125rem' }}>Mon Compte</h2>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem' }}>Gérer ton profil et tes statistiques</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 34, height: 34, borderRadius: '50%',
                            background: 'var(--glass)', border: '1px solid var(--glass-border)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--text-3)', cursor: 'pointer', transition: 'all 200ms ease',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--glass-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--glass)'}
                    >
                        <X style={{ width: 16, height: 16 }} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', padding: '0 24px', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
                    {[
                        { key: 'settings', label: 'Paramètres', icon: <Settings style={{ width: 15, height: 15 }} /> },
                        { key: 'stats', label: 'Mes Stats', icon: <BarChart3 style={{ width: 15, height: 15 }} /> },
                        { key: 'changelog', label: 'Patch Notes', icon: <FileText style={{ width: 15, height: 15 }} /> },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '14px 16px',
                                background: 'none', border: 'none',
                                color: activeTab === tab.key ? 'var(--text-1)' : 'var(--text-4)',
                                fontWeight: activeTab === tab.key ? 600 : 400,
                                fontSize: '0.9375rem',
                                cursor: 'pointer',
                                borderBottom: `2px solid ${activeTab === tab.key ? 'var(--accent)' : 'transparent'}`,
                                marginBottom: -1,
                                transition: 'all 150ms ease',
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {activeTab === 'settings' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {error && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderRadius: 'var(--r-md)', background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5', fontSize: '0.875rem' }}>
                                    <AlertCircle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }} />
                                    {error}
                                </div>
                            )}

                            {/* User info */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Mail style={{ width: 18, height: 18, color: 'var(--text-4)' }} />
                                        <div>
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginBottom: 2 }}>Nom d'utilisateur</p>
                                            <p style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem' }}>{user.username}</p>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Lock style={{ width: 18, height: 18, color: 'var(--text-4)' }} />
                                        <div>
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginBottom: 2 }}>Mot de passe</p>
                                            <p style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem' }}>
                                                {showPassword
                                                    ? <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: '0.8125rem' }}>Stocké en sécurité côté serveur</span>
                                                    : '••••••••'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ padding: 8, background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', borderRadius: 8, transition: 'color 150ms ease' }}
                                        onMouseEnter={e => e.currentTarget.style.color = 'var(--text-2)'}
                                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}
                                    >
                                        {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
                                    </button>
                                </div>
                            </div>

                            {/* Compte Discord */}
                            <div style={{ paddingTop: 8, borderTop: '1px solid var(--glass-border)' }}>
                                <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <Link2 style={{ width: 18, height: 18, color: 'var(--accent-bright)' }} />
                                    Compte Discord
                                </h3>

                                {discordError && (
                                    <div style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5', fontSize: '0.8125rem' }}>
                                        {discordError}
                                    </div>
                                )}

                                {discordLink?.linked ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                                        <div>
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginBottom: 2 }}>Lié à</p>
                                            <p style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem' }}>{discordLink.discord_username}</p>
                                        </div>
                                        <button
                                            onClick={handleUnlinkDiscord}
                                            disabled={linkingDiscord}
                                            style={{ background: 'none', border: 'none', color: '#FCA5A5', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', padding: 0, opacity: linkingDiscord ? 0.5 : 1 }}
                                        >
                                            {linkingDiscord ? 'Déliaison...' : '× Délier'}
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                                        <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem', marginBottom: 10 }}>
                                            Tape <code style={{ background: 'var(--glass-active)', padding: '2px 6px', borderRadius: 4 }}>/link</code> sur le bot Discord du serveur pour obtenir un code, puis colle-le ici (valable 15 min).
                                        </p>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input
                                                type="text"
                                                value={discordCode}
                                                onChange={(e) => setDiscordCode(e.target.value)}
                                                placeholder="Code reçu sur Discord"
                                                maxLength={8}
                                                style={{
                                                    flex: 1, padding: '10px 12px', borderRadius: 'var(--r-md)',
                                                    background: 'var(--glass-active)', border: '1px solid var(--glass-border)',
                                                    color: 'var(--text-1)', fontSize: '0.875rem', textTransform: 'uppercase',
                                                }}
                                            />
                                            <button
                                                onClick={handleConfirmDiscordLink}
                                                disabled={linkingDiscord || !discordCode.trim()}
                                                style={{
                                                    padding: '10px 16px', borderRadius: 'var(--r-md)', border: 'none',
                                                    background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: '0.875rem',
                                                    cursor: linkingDiscord || !discordCode.trim() ? 'default' : 'pointer',
                                                    opacity: linkingDiscord || !discordCode.trim() ? 0.5 : 1,
                                                }}
                                            >
                                                {linkingDiscord ? '...' : 'Lier'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sessions */}
                            <div style={{ paddingTop: 8, borderTop: '1px solid var(--glass-border)' }}>
                                <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <Globe style={{ width: 18, height: 18, color: 'var(--accent-bright)' }} />
                                    Appareils connectés
                                </h3>

                                {loadingSessions ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent-glow)', borderTop: '2px solid var(--accent)', animation: 'spinSlow 0.8s linear infinite' }} />
                                    </div>
                                ) : sessions.length === 0 ? (
                                    <div style={{ padding: '24px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)', textAlign: 'center', color: 'var(--text-4)' }}>
                                        Aucun appareil connecté
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {sessions.map((session) => (
                                            <div
                                                key={session.id ?? session.token}
                                                style={{
                                                    padding: '14px 16px',
                                                    borderRadius: 'var(--r-lg)',
                                                    background: session.isCurrent ? 'linear-gradient(135deg, rgba(193,145,60,0.15), rgba(232,183,90,0.08))' : 'var(--glass)',
                                                    border: `1px solid ${session.isCurrent ? 'rgba(232,183,90,0.3)' : 'var(--glass-border)'}`,
                                                    display: 'flex', alignItems: 'flex-start', gap: 14,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                                        background: session.isCurrent ? 'linear-gradient(135deg, var(--accent-deep), var(--accent))' : 'var(--glass-active)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: session.isCurrent ? '#fff' : 'var(--text-3)',
                                                    }}
                                                >
                                                    {getDeviceIcon(session.device, session.isPWA)}
                                                </div>

                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                                                        <div>
                                                            <h4 style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
                                                                {session.browser} · {session.os}
                                                                {session.isPWA && (
                                                                    <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 99, background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', color: '#93C5FD', fontSize: 10, fontWeight: 700 }}>PWA</span>
                                                                )}
                                                            </h4>
                                                            <p style={{ color: 'var(--text-4)', fontSize: '0.75rem' }}>
                                                                {session.device === 'mobile' ? '📱 Mobile' : '💻 Ordinateur'}
                                                            </p>
                                                        </div>
                                                        {session.isCurrent && (
                                                            <span style={{ padding: '3px 10px', borderRadius: 99, background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.3)', color: '#6EE7B7', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                                                Actuel
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div style={{ color: 'var(--text-4)', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <span>IP : {session.ip_address}</span>
                                                        <span>Activité : {formatDate(session.last_used)}</span>
                                                    </div>

                                                    {!session.isCurrent && (
                                                        <button
                                                            onClick={() => handleDisconnectSession(session)}
                                                            disabled={disconnectingToken === (session.id ?? session.token)}
                                                            style={{ marginTop: 8, background: 'none', border: 'none', color: '#FCA5A5', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: 0, opacity: disconnectingToken === (session.id ?? session.token) ? 0.5 : 1 }}
                                                        >
                                                            {disconnectingToken === (session.id ?? session.token) ? 'Déconnexion...' : '× Déconnecter cet appareil'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Logout button */}
                            <div style={{ paddingTop: 8, borderTop: '1px solid var(--glass-border)' }}>
                                <button
                                    onClick={() => setShowConfirmLogout(true)}
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                        padding: '14px 20px', borderRadius: 'var(--r-lg)',
                                        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                                        color: '#FCA5A5', fontWeight: 600, fontSize: '0.9375rem',
                                        cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
                                        transition: 'all 200ms ease',
                                    }}
                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; }}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                                >
                                    <LogOut style={{ width: 18, height: 18 }} />
                                    Se déconnecter de toutes les sessions
                                </button>
                                <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', textAlign: 'center', marginTop: 8 }}>
                                    Vous serez déconnecté de tous vos appareils
                                </p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'stats' && (
                        <UserStats user={user} />
                    )}

                    {activeTab === 'changelog' && (
                        <PatchNotesPage />
                    )}
                </div>
            </div>

            {/* Confirm logout modal */}
            {showConfirmLogout && (
                <div
                    style={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                    }}
                >
                    <div
                        style={{
                            background: 'var(--bg-modal)',
                            borderRadius: 'var(--r-xl)',
                            padding: '28px 24px',
                            maxWidth: 380, width: '100%',
                            border: '1px solid rgba(239,68,68,0.2)',
                            boxShadow: 'var(--shadow-xl)',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AlertTriangle style={{ width: 24, height: 24, color: '#EF4444' }} />
                            </div>
                        </div>
                        <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.125rem', textAlign: 'center', marginBottom: 10 }}>
                            Déconnexion de toutes les sessions
                        </h3>
                        <p style={{ color: 'var(--text-3)', fontSize: '0.875rem', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
                            Vous devrez vous reconnecter sur tous vos appareils.
                        </p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowConfirmLogout(false)} className="btn-ghost" style={{ flex: 1, padding: '12px 16px', fontSize: '0.9375rem' }}>
                                Annuler
                            </button>
                            <button
                                onClick={handleLogoutAllSessions}
                                disabled={loading}
                                style={{
                                    flex: 1, padding: '12px 16px', borderRadius: 'var(--r-md)',
                                    background: '#DC2626', border: 'none', color: '#fff',
                                    fontWeight: 700, fontSize: '0.9375rem', cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.5 : 1, transition: 'all 200ms ease',
                                }}
                                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#B91C1C'; }}
                                onMouseLeave={e => e.currentTarget.style.background = '#DC2626'}
                            >
                                {loading ? 'Déconnexion...' : 'Confirmer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsModal;